import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  CalendarMonth as CalendarMonthIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const PricingGrid = ({ listing }) => {
  const { t } = useTranslation('booking');
  
  const formatPrice = (price, currency = 'EUR') => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const pricingOptions = [
    {
      period: t('pricingGrid.periods.hourly'),
      price: listing?.price_hourly,
      icon: <ScheduleIcon sx={{ fontSize: 20 }} />,
      color: '#e3f2fd',
      iconColor: '#1976d2',
      unit: t('pricingGrid.units.hour'),
      description: t('pricingGrid.descriptions.hourly')
    },
    {
      period: t('pricingGrid.periods.daily'),
      price: listing?.price_daily,
      icon: <TodayIcon sx={{ fontSize: 20 }} />,
      color: '#f3e5f5',
      iconColor: '#7b1fa2',
      unit: t('pricingGrid.units.day'),
      description: t('pricingGrid.descriptions.daily'),
      popular: true
    },
    {
      period: t('pricingGrid.periods.weekly'),
      price: listing?.price_weekly,
      icon: <DateRangeIcon sx={{ fontSize: 20 }} />,
      color: '#e8f5e8',
      iconColor: '#388e3c',
      unit: t('pricingGrid.units.week'),
      description: t('pricingGrid.descriptions.weekly')
    },
    {
      period: t('pricingGrid.periods.monthly'),
      price: listing?.price_monthly,
      icon: <CalendarMonthIcon sx={{ fontSize: 20 }} />,
      color: '#fff3e0',
      iconColor: '#f57c00',
      unit: t('pricingGrid.units.month'),
      description: t('pricingGrid.descriptions.monthly')
    }
  ];

  const availablePricing = pricingOptions.filter(option => option.price && option.price > 0);

  if (availablePricing.length === 0) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('pricingGrid.title')}
          </Typography>
          <Typography color="text.secondary">
            {t('pricingGrid.notAvailable')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('pricingGrid.title')}
          </Typography>
          <Tooltip title={t('pricingGrid.indicativeTooltip')}>
            <InfoIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          </Tooltip>
        </Box>
        
        <Grid container spacing={2}>
          {availablePricing.map((option, index) => (
            <Grid item xs={12} sm={6} md={availablePricing.length > 2 ? 6 : 12} key={index}>
              <Card 
                variant="outlined" 
                sx={{ 
                  position: 'relative',
                  backgroundColor: option.color,
                  border: option.popular ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 2
                  }
                }}
              >
                {option.popular && (
                  <Chip
                    label="Populaire"
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: 8,
                      fontSize: '0.75rem'
                    }}
                  />
                )}
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        color: option.iconColor,
                        boxShadow: 1
                      }}
                    >
                      {option.icon}
                    </Box>
                  </Box>
                  
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    {option.period}
                  </Typography>
                  
                  <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                    {formatPrice(option.price, listing?.currency)}
                  </Typography>
                  
                  <Typography variant="caption" color="text.secondary">
                    {option.unit}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Section Caution */}
        {listing?.deposit_amount && listing.deposit_amount > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#fff3e0', 
              borderRadius: 1,
              border: '1px solid #ffcc02',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 40, 
                height: 40, 
                borderRadius: '50%',
                backgroundColor: '#ffcc02',
                color: 'white'
              }}>
                🛡️
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1" fontWeight="medium" sx={{ color: '#f57c00' }}>
                  {t('pricingGrid.deposit.required')}
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#ef6c00' }}>
                  {formatPrice(listing.deposit_amount, listing?.currency)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('pricingGrid.deposit.refundable')}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
          <Typography variant="caption" color="text.secondary">
            {t('pricingGrid.finalCalculationNote')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PricingGrid;
